import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ShieldCheck, Loader2, X, Plus, Trash2, AlertTriangle, Mail, Crown } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { MemberColorPicker } from "./MemberColorPicker";
import { defaultMemberColor } from "../lib/memberColors";
import { isPrimaryPoolOwner, isPoolCoOwner } from "../lib/poolMembers";

function newDraftMember(index = 0) {
    return {
        key: crypto.randomUUID(),
        id: null,
        name: "",
        role_label: "",
        email: "",
        percentage: "",
        color: defaultMemberColor(index),
        isNew: true,
        isOwner: false,
        invite_user: false,
    };
}

function mapActiveMembers(pool) {
    return (pool?.members || [])
        .filter((m) => (m.status ?? "active") === "active")
        .map((m) => ({
            key: m.id,
            id: m.id,
            name: m.name || "",
            role_label: m.role_label || "",
            email: m.email || "",
            percentage: String(m.percentage ?? ""),
            color: m.color,
            isNew: false,
            isOwner: Boolean(m.is_owner),
            isPrimaryOwner: isPrimaryPoolOwner(m, pool),
            isCoOwner: isPoolCoOwner(m, pool),
        }));
}

export function EditPercentagesDialog({ pool, open, onClose, onChanged }) {
    const { activeOrg, user } = useAuth();
    const [rows, setRows] = useState(() => mapActiveMembers(pool));
    const [busy, setBusy] = useState(false);
    const [memberHeadroom, setMemberHeadroom] = useState(null);

    useEffect(() => {
        if (open) setRows(mapActiveMembers(pool));
    }, [open, pool]);

    useEffect(() => {
        if (!open || !activeOrg?.id) return;
        let mounted = true;
        api.get("/billing/subscription", { params: { org_id: activeOrg.id } })
            .then(({ data }) => {
                if (!mounted) return;
                const limit = data?.limits?.max_pool_members ?? null;
                const used = data?.usage?.pool_members ?? 0;
                const activeCount = (pool?.members || []).filter((m) => (m.status ?? "active") === "active").length;
                setMemberHeadroom(limit == null ? null : Math.max(0, limit - used + activeCount));
            })
            .catch(() => {
                if (mounted) setMemberHeadroom(null);
            });
        return () => { mounted = false; };
    }, [open, activeOrg?.id, pool]);

    if (!open) return null;

    const visibleRows = rows.filter((r) => !r.removed);
    const activeRows = visibleRows.filter((r) => !r.pendingRemoval);
    const pendingRemovalRows = visibleRows.filter((r) => r.pendingRemoval);
    const slotsUsed = visibleRows.length;
    const total = activeRows.reduce((s, r) => s + (parseFloat(r.percentage) || 0), 0);
    const valid = Math.abs(total - 100) < 0.01;
    const canAddMember = memberHeadroom == null || slotsUsed < memberHeadroom;
    const hasUnresolvedRemovals = pendingRemovalRows.length > 0;

    const updateRow = (key, patch) => {
        setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    };

    const toggleInvite = (key) => {
        setRows((prev) => prev.map((r) => {
            if (r.key !== key || !r.isNew) return r;
            const nextInvite = !r.invite_user;
            return { ...r, invite_user: nextInvite };
        }));
    };

    const addRow = () => {
        if (!canAddMember) {
            toast.error("Free plan limit reached", {
                description: pendingRemovalRows.length
                    ? "Confirm a pending removal first, or upgrade to add more members."
                    : "Remove a member first, or upgrade to add more pool members.",
            });
            return;
        }
        setRows((prev) => [...prev, newDraftMember(prev.length)]);
    };

    const requestRemoveRow = (row) => {
        if (row.isPrimaryOwner) {
            toast.error("Cannot remove the pool owner");
            return;
        }
        if (row.isCoOwner && pool?.owner_user_id !== user?.id) {
            toast.error("Only the pool creator can remove a co-owner");
            return;
        }
        if (activeRows.length <= 1) {
            toast.error("At least one pool member is required");
            return;
        }
        if (row.isNew) {
            setRows((prev) => prev.filter((r) => r.key !== row.key));
            return;
        }
        updateRow(row.key, { pendingRemoval: true });
    };

    const confirmRemoveRow = (row) => {
        updateRow(row.key, { pendingRemoval: false, removed: true, percentage: "0" });
        toast.message("Member marked for removal", {
            description: "Submit your allocation change to apply. You can add a replacement member now.",
        });
    };

    const cancelRemoveRow = (row) => {
        updateRow(row.key, { pendingRemoval: false });
    };

    const submit = async () => {
        if (hasUnresolvedRemovals) {
            toast.error("Confirm or cancel pending removals", {
                description: "Each member marked for removal must be confirmed or cancelled before submitting.",
            });
            return;
        }
        if (!valid) {
            toast.error(`Percentages must sum to 100% (currently ${total.toFixed(2)}%)`);
            return;
        }
        for (const row of activeRows) {
            if (!row.name.trim()) {
                toast.error("Every member needs a name");
                return;
            }
            if (row.isNew && row.invite_user && !row.email.trim()) {
                toast.error(`Email is required to invite ${row.name.trim()}`);
                return;
            }
        }
        try {
            setBusy(true);
            const activeIds = new Set(activeRows.filter((r) => r.id).map((r) => r.id));
            const removedIds = new Set(rows.filter((r) => r.removed && r.id).map((r) => r.id));
            const implicitRemovals = (pool?.members || [])
                .filter((m) => (m.status ?? "active") === "active" && m.id && !activeIds.has(m.id) && !removedIds.has(m.id))
                .map((m) => ({
                    id: m.id,
                    percentage: parseFloat(m.percentage) || 0,
                    remove: true,
                }));

            const payload = {
                pool_id: pool.id,
                members: [
                    ...rows.filter((r) => r.removed && r.id).map((r) => ({
                        id: r.id,
                        percentage: parseFloat(r.percentage) || 0,
                        remove: true,
                    })),
                    ...implicitRemovals,
                    ...activeRows.map((r) => ({
                        id: r.id || undefined,
                        name: r.name.trim(),
                        role_label: r.role_label,
                        email: r.email || undefined,
                        percentage: parseFloat(r.percentage),
                        color: r.color,
                        remove: false,
                        invite_user: Boolean(r.isNew && r.invite_user),
                    })),
                ],
            };
            const { data } = await api.post(`/pools/${pool.id}/allocations`, payload);
            const inviteResults = data.invitations || [];
            const sentCount = inviteResults.filter((r) => r.ok).length;
            const failedInvites = inviteResults.filter((r) => !r.ok);
            const pendingInvites = activeRows.filter((r) => r.isNew && r.invite_user).length;
            if (data.status === "applied") {
                toast.success("Allocations updated", {
                    description: sentCount
                        ? `${sentCount} invitation${sentCount === 1 ? "" : "s"} sent`
                        : undefined,
                });
            } else {
                toast.success("Approval requested", {
                    description: pendingInvites
                        ? `An Admin must approve before this takes effect. ${pendingInvites} invite${pendingInvites === 1 ? "" : "s"} will send after approval.`
                        : "An Admin must approve before this takes effect.",
                });
            }
            if (failedInvites.length) {
                toast.error("Some invitations failed", {
                    description: failedInvites.map((r) => `${r.email}: ${r.error}`).join("; "),
                });
            }
            onChanged?.(data);
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    return (
        <Dialog onClose={onClose} title="Update allocations" subtitle="Adjust shares, colours, and pool members. Changes may require approval." testid="edit-dialog" size="xl">
            <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <p className="text-xs text-white/45">Active members in this pool</p>
                        {memberHeadroom != null && (
                            <p className="mt-0.5 text-[10px] text-white/35">
                                {slotsUsed} of {memberHeadroom} member slot{memberHeadroom === 1 ? "" : "s"} in use
                                {pendingRemovalRows.length > 0 && " · confirm removals to free slots"}
                            </p>
                        )}
                    </div>
                    <button type="button" onClick={addRow} disabled={!canAddMember} className="text-xs text-[#00BF0D] hover:text-white inline-flex items-center gap-1 disabled:opacity-40" data-testid="edit-add-member">
                        <Plus className="h-3.5 w-3.5" /> Add member
                    </button>
                </div>
                {visibleRows.map((row, idx) => (
                    <div key={row.key} className="flex items-start gap-2.5">
                        <div className="flex w-7 shrink-0 items-center self-center">
                            {row.isPrimaryOwner && (
                                <span
                                    className="inline-flex text-[#00BF0D]"
                                    title="Pool owner"
                                    aria-label="Pool owner"
                                    data-testid={`edit-owner-badge-${row.id || idx}`}
                                >
                                    <Crown className="h-4 w-4" strokeWidth={2.25} />
                                </span>
                            )}
                        </div>
                    <div
                        className={`edit-allocation-row min-w-0 flex-1 rounded-xl border p-3 ${
                            row.pendingRemoval
                                ? "border-amber-500/35 bg-amber-500/[0.06]"
                                : "border-white/[0.08] bg-white/[0.02]"
                        }`}
                    >
                        {row.pendingRemoval ? (
                            <div className="space-y-3" data-testid={`edit-pending-removal-${row.id || idx}`}>
                                <div className="flex items-start gap-2.5">
                                    <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-amber-100/90">
                                            Remove {row.name.trim() || "this member"}?
                                        </p>
                                        <p className="mt-1 text-xs text-white/50">
                                            Confirming frees a member slot so you can add a replacement. The removal is only applied when you submit your allocation change.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() => confirmRemoveRow(row)}
                                        className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-xs font-medium text-rose-300 hover:bg-rose-500/20"
                                        data-testid={`edit-confirm-remove-${row.id || idx}`}
                                    >
                                        Confirm removal
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => cancelRemoveRow(row)}
                                        className="rounded-md border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-white/70 hover:bg-white/[0.06]"
                                        data-testid={`edit-cancel-remove-${row.id || idx}`}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                        <>
                        <div className="edit-allocation-row__grid">
                            <div className="edit-allocation-row__color">
                                <span className="edit-allocation-field-label">Colour</span>
                                <MemberColorPicker
                                    value={row.color}
                                    onChange={(color) => updateRow(row.key, { color })}
                                    testId={`edit-color-${row.id || idx}`}
                                />
                            </div>
                            <label className="edit-allocation-row__name block min-w-0">
                                <span className="edit-allocation-field-label">Name</span>
                                <input
                                    value={row.name}
                                    onChange={(e) => updateRow(row.key, { name: e.target.value })}
                                    className="input-dark w-full rounded-md px-2.5 py-2 text-xs"
                                    data-testid={`edit-name-${row.id || idx}`}
                                />
                            </label>
                            <label className="edit-allocation-row__role block min-w-0">
                                <span className="edit-allocation-field-label">Role</span>
                                <input
                                    value={row.role_label}
                                    onChange={(e) => updateRow(row.key, { role_label: e.target.value })}
                                    className="input-dark w-full rounded-md px-2.5 py-2 text-xs"
                                    data-testid={`edit-role-${row.id || idx}`}
                                />
                            </label>
                            <label className="edit-allocation-row__email block min-w-0">
                                <span className="edit-allocation-field-label">Email</span>
                                <input
                                    type="email"
                                    value={row.email}
                                    onChange={(e) => updateRow(row.key, { email: e.target.value })}
                                    placeholder={row.isNew && row.invite_user ? "Email (required for invite)" : "Optional"}
                                    required={row.isNew && row.invite_user}
                                    className="input-dark w-full rounded-md px-2.5 py-2 text-xs"
                                    data-testid={`edit-email-${row.id || idx}`}
                                />
                            </label>
                            <label className="edit-allocation-row__pct block min-w-0">
                                <span className="edit-allocation-field-label">Share %</span>
                                <div className="flex items-center gap-1">
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        value={row.percentage}
                                        onChange={(e) => updateRow(row.key, { percentage: e.target.value })}
                                        className="input-dark w-full rounded-md px-2.5 py-2 text-right font-mono text-xs"
                                        data-testid={`edit-pct-${row.id || idx}`}
                                    />
                                    <span className="text-white/40 text-xs">%</span>
                                </div>
                            </label>
                            <div className="edit-allocation-row__remove flex items-end justify-end gap-1">
                                {row.isNew && (
                                    <button
                                        type="button"
                                        onClick={() => toggleInvite(row.key)}
                                        className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
                                            row.invite_user
                                                ? "text-[#00BF0D] hover:bg-[#00BF0D]/10"
                                                : "text-white/40 hover:bg-white/[0.06] hover:text-white/70"
                                        }`}
                                        aria-pressed={row.invite_user}
                                        title="Send email invite"
                                        data-testid={`edit-invite-${idx}`}
                                    >
                                        <Mail className="h-3.5 w-3.5" />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => requestRemoveRow(row)}
                                    disabled={
                                        row.isPrimaryOwner
                                        || activeRows.length <= 1
                                        || (row.isCoOwner && pool?.owner_user_id !== user?.id)
                                    }
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-white/40 transition-colors hover:bg-rose-500/10 hover:text-rose-400 disabled:opacity-30"
                                    data-testid={`edit-remove-${row.id || idx}`}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                        {row.isCoOwner && !row.isPrimaryOwner && (
                            <p className="mt-2 text-[10px] uppercase tracking-wide text-[#00BF0D]/50">Co-owner</p>
                        )}
                        {row.isNew && row.invite_user && (
                            <p className="mt-2 text-[10px] text-white/45">An invitation email will be sent when this change is applied.</p>
                        )}
                        </>
                        )}
                    </div>
                    </div>
                ))}
                <div className={`flex justify-between pt-3 border-t border-white/[0.06] text-sm ${valid ? "text-[#00BF0D]" : "text-rose-400"}`}>
                    <span>Total</span>
                    <span className="font-mono font-semibold">{total.toFixed(2)}%</span>
                </div>
                <div className="rounded-lg border border-white/[0.06] bg-white/[0.025] p-3 flex items-start gap-2.5">
                    <ShieldCheck className="h-4 w-4 text-[#00BF0D] mt-0.5 shrink-0" />
                    <p className="text-xs text-white/55">
                        If another Admin or Owner exists in this workspace, your change will be queued for their approval before applying. Otherwise it applies instantly. Every change is recorded in the audit log.
                    </p>
                </div>
            </div>
            <DialogFooter>
                <button onClick={onClose} className="btn-ghost rounded-md px-4 py-2 text-sm" data-testid="edit-cancel">Cancel</button>
                <button onClick={submit} disabled={busy || !valid || hasUnresolvedRemovals} className="btn-neon rounded-md px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="edit-confirm">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                    {busy ? "Submitting…" : "Submit change"}
                </button>
            </DialogFooter>
        </Dialog>
    );
}

export function Dialog({ children, onClose, title, subtitle, testid, size = "md" }) {
    const widths = { sm: "max-w-sm", md: "max-w-md", lg: "max-w-lg", xl: "max-w-2xl", profile: "max-w-4xl" };

    return createPortal(
        <div className="app-dialog" data-testid={testid} role="dialog" aria-modal="true" aria-labelledby={`${testid}-title`}>
            <button type="button" className="app-dialog__backdrop" aria-label="Close dialog" onClick={onClose} />
            <div className="app-dialog__viewport">
                <div className={`app-dialog__panel surface ${widths[size]}`}>
                    <button type="button" onClick={onClose} className="absolute top-3 right-3 inline-flex h-7 w-7 items-center justify-center rounded-md text-white/55 hover:bg-white/[0.06] hover:text-white" data-testid={`${testid}-close`}>
                        <X className="h-4 w-4" />
                    </button>
                    <h3 id={`${testid}-title`} className="pr-8 font-heading text-xl tracking-tight">{title}</h3>
                    {subtitle ? <p className="mt-1 text-sm text-white/50">{subtitle}</p> : null}
                    <div className="mt-5">{children}</div>
                </div>
            </div>
        </div>,
        document.body,
    );
}

export function DialogFooter({ children }) {
    return <div className="mt-6 flex gap-2 justify-end">{children}</div>;
}
